/**
 * File upload size limits and type restrictions
 * Centralized constants for consistent file validation across the application
 */

// ============================================
// File Size Limits (in bytes)
// ============================================

/**
 * Audio file upload limit: 1000 MB (1 GB)
 * Used in: upload-audio-dialog.tsx
 */
export const AUDIO_MAX_SIZE_BYTES = 1000 * 1024 * 1024;

/**
 * PDF file upload limit: 10 MB
 * Used in: form-filling-upload.tsx
 */
export const PDF_MAX_SIZE_BYTES = 10 * 1024 * 1024;

/**
 * Derived from PDF_MAX_SIZE_BYTES to avoid drift.
 */
export const PDF_MAX_SIZE_MB = PDF_MAX_SIZE_BYTES / (1024 * 1024);

/**
 * CSV file upload limit: 10 MB
 * Used in: replacements upload, snippets upload, session schedule upload
 */
export const CSV_MAX_SIZE_BYTES = 10 * 1024 * 1024;

/**
 * Profile image upload limit: 5 MB
 * Used in: useProfileImageUploader.ts
 */
export const PROFILE_IMAGE_MAX_SIZE_BYTES = 5 * 1024 * 1024;

// ============================================
// File Type Restrictions (MIME types)
// ============================================

/**
 * Allowed profile image MIME types
 * Used in: useProfileImageUploader.ts
 */
export const PROFILE_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/jpg'] as const;

/**
 * Allowed audio file MIME types
 * Single source of truth for audio format support
 * Used in: upload-audio-dialog.tsx for validation and accept attribute
 */
export const AUDIO_MIME_TYPES = [
  'audio/mpeg',
  'audio/mp3',
  'audio/x-m4a',
  'audio/m4a',
  'audio/wav',
  'audio/x-wav',
  'audio/ogg',
  'application/ogg', // OGG files may be identified as application/ogg on some systems
  'audio/flac',
  'audio/x-flac', // FLAC files may use x-flac prefix on some systems
  'audio/mp4',
  'audio/aac',
  'audio/aacp',
  'audio/x-aac', // AAC files may use x-aac prefix on some systems
  'audio/x-ms-wma', // WMA (Windows Media Audio)
  'audio/wma', // WMA alternative MIME type
] as const;

/**
 * Audio file extensions for accept attribute
 * Common extensions that correspond to AUDIO_MIME_TYPES
 */
export const AUDIO_FILE_EXTENSIONS = [
  '.m4a',
  '.mp3',
  '.wav',
  '.ogg',
  '.flac',
  '.mp4',
  '.aac',
  '.wma',
] as const;

/**
 * Mapping of audio file extensions to their correct MIME types
 * Used to fix incorrect MIME types detected by different operating systems
 * (e.g., some systems may report .ogg files as application/ogg instead of audio/ogg)
 */
export const AUDIO_EXTENSION_TO_MIME_TYPE: Record<string, string> = {
  '.ogg': 'audio/ogg',
  '.mp3': 'audio/mpeg',
  '.wav': 'audio/wav',
  '.m4a': 'audio/m4a',
  '.aac': 'audio/aac',
  '.flac': 'audio/flac',
  '.mp4': 'audio/mp4',
  '.wma': 'audio/x-ms-wma',
} as const;

/**
 * Supported session schedule file formats (CSV + preview images).
 */
export const SESSION_SCHEDULE_MIME_TYPES = [
  'text/csv',
  'image/png',
  'image/jpeg',
] as const;

/**
 * Supported session schedule formats including PDF — gated by the
 * `release-scribe-enable-schedule-upload-pdf` Flagsmith flag while the
 * backend PDF parser is rolled out.
 */
export const SESSION_SCHEDULE_MIME_TYPES_WITH_PDF = [
  ...SESSION_SCHEDULE_MIME_TYPES,
  'application/pdf',
] as const;

/**
 * Session schedule upload file input `accept` value (MIME types + extensions).
 */
export const SESSION_SCHEDULE_ACCEPT_FORMATS = [
  ...SESSION_SCHEDULE_MIME_TYPES,
  '.csv',
  '.png',
  '.jpg',
  '.jpeg',
].join(',');

/**
 * Session schedule upload `accept` value with PDF support — paired with
 * `SESSION_SCHEDULE_MIME_TYPES_WITH_PDF` and gated by the same flag.
 */
export const SESSION_SCHEDULE_ACCEPT_FORMATS_WITH_PDF = [
  ...SESSION_SCHEDULE_MIME_TYPES_WITH_PDF,
  '.csv',
  '.png',
  '.jpg',
  '.jpeg',
  '.pdf',
].join(',');

// ============================================
// File Type Restrictions (Accept attributes)
// ============================================

/**
 * Audio file accept attribute value
 * Auto-generated from AUDIO_MIME_TYPES and AUDIO_FILE_EXTENSIONS
 * Supports: .m4a, .mp3, .wav, .ogg, .flac, .mp4, .aac, .wma
 * Used in: upload-audio-dialog.tsx
 */
export const AUDIO_ACCEPT_FORMATS = [...AUDIO_MIME_TYPES, ...AUDIO_FILE_EXTENSIONS].join(
  ','
);

/**
 * CSV file accept attribute value
 * Used in: replacements-upload-modal.tsx
 */
export const CSV_ACCEPT_FORMATS = '.csv,text/csv';

/**
 * PDF file accept attribute value
 * Used in: form-filling-upload.tsx
 */
export const PDF_ACCEPT_FORMATS = 'application/pdf';

/**
 * Word (.docx) MIME type — used to detect docx form-template uploads.
 */
export const DOCX_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/**
 * XML form-template upload limit: 2 MB.
 * Must match the backend's XML size ceiling — the extract-fields endpoint
 * rejects anything larger with a 400, so the client-side check exists to fail
 * on selection instead of after the round-trip.
 * Used in: form-filling-upload.tsx
 */
export const XML_MAX_SIZE_BYTES = 2 * 1024 * 1024;

/**
 * Human-readable max size for XML form templates (for the error banner).
 * Derived from XML_MAX_SIZE_BYTES to avoid drift.
 */
export const XML_MAX_SIZE_MB = XML_MAX_SIZE_BYTES / (1024 * 1024);

/**
 * XML MIME types. Browsers are inconsistent about `.xml` — Chrome reports
 * `text/xml`, Safari `application/xml`, and a file dragged from some archive
 * tools arrives with an empty type — so the extension is part of the accept
 * record and `isXmlFile` checks both.
 */
export const XML_MIME_TYPES = ['application/xml', 'text/xml'] as const;

/**
 * Extract cap for a submitted JSON form. The backend reads the upload with
 * `MAX_SUBMITTED_JSON_SOURCE_CHARS` (500_000 bytes). The 2 MB storage cap is
 * larger and is not what extract rejects against.
 */
export const JSON_MAX_SUBMITTED_BYTES = 500_000;

/**
 * Human-readable JSON extract cap (for the error banner). Decimal KB, because
 * the backend cap is a decimal 500_000.
 */
export const JSON_MAX_SUBMITTED_KB = JSON_MAX_SUBMITTED_BYTES / 1000;

/**
 * JSON MIME types for the dropzone. react-dropzone admits a file whose MIME
 * type OR extension matches any entry, so a `.json` reported as `''` or
 * `text/plain` already passes via `.json`; listing `text/plain` or
 * `application/octet-stream` here would admit every `.txt` and unknown file.
 */
export const JSON_MIME_TYPES = ['application/json', 'text/json'] as const;

/**
 * The form-template dropzone's `accept` record, composed from the flags and
 * build profile that widen it. Returning the record (never a comma-separated
 * string) is what keeps react-dropzone from bucketing extension-only entries
 * under an empty MIME key and logging `Skipped "" because it is not a valid
 * MIME type`.
 *
 * `.docx` rides `release-growth-enable-docx-form` and `.xml` rides
 * `release-growth-enable-xml-form`. PDF was unconditional until on-prem gained
 * forms: PDF form filling attaches the document to a multimodal LLM call, which
 * the appliance's local text-only gateway cannot serve, so on-prem is XML-only
 * and `acceptsPdf` is how the caller says so.
 */
export const getFormTemplateAccept = ({
  acceptsPdf = true,
  acceptsDocx,
  acceptsXml,
  acceptsJson = false,
}: {
  acceptsPdf?: boolean;
  acceptsDocx: boolean;
  acceptsXml: boolean;
  acceptsJson?: boolean;
}): Record<string, readonly string[]> => ({
  ...(acceptsPdf ? { 'application/pdf': ['.pdf'] } : {}),
  ...(acceptsDocx ? { [DOCX_MIME_TYPE]: ['.docx'] } : {}),
  ...(acceptsXml
    ? Object.fromEntries(XML_MIME_TYPES.map((mimeType) => [mimeType, ['.xml']]))
    : {}),
  ...(acceptsJson
    ? Object.fromEntries(JSON_MIME_TYPES.map((mimeType) => [mimeType, ['.json']]))
    : {}),
});

/**
 * Patient-document accept value as a MIME-to-extension record — the native
 * shape `FileUploadButton` / `FileDropzone` expect.
 *
 * Narrower than `CONTEXT_DOCUMENTS_ACCEPT`: patient documents are a persistent
 * clinical record summarized through the BE-1c extract→summarize pipeline,
 * which only produces a usable summary for PDF and Word. Images (png/jpg) and
 * text/plain are intentionally excluded here — they previously uploaded but
 * never became "available for context". Images stay a session-scoped concern,
 * handled by the in-session uploader (`CONTEXT_DOCUMENTS_ACCEPT`).
 * Must match backend ALLOWED_PATIENT_DOCUMENT_MIME_TYPES in
 * domains/patient_document/constants.py.
 */
export const PATIENT_DOCUMENTS_ACCEPT = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
} as const satisfies Record<string, readonly string[]>;

/**
 * Patient-document accept attribute value (comma-separated extension string)
 * for native `<input type="file">` elements that don't take the record shape.
 * Mirrors `PATIENT_DOCUMENTS_ACCEPT`.
 */
export const PATIENT_DOCUMENTS_ACCEPT_FORMATS = '.pdf,.doc,.docx';

/**
 * Patient-document file size limit: 25 MB
 * Must match backend MAX_PATIENT_DOCUMENT_SIZE_BYTES in
 * domains/patient_document/constants.py. Enforced client-side before upload so
 * oversized files are rejected on selection instead of erroring after the
 * round-trip.
 */
export const MAX_PATIENT_DOCUMENT_SIZE_BYTES = 25 * 1024 * 1024;

/**
 * Human-readable max size for patient documents (for helper text + errors).
 * Derived from MAX_PATIENT_DOCUMENT_SIZE_BYTES to avoid drift.
 */
export const MAX_PATIENT_DOCUMENT_SIZE_MB =
  MAX_PATIENT_DOCUMENT_SIZE_BYTES / (1024 * 1024);

/**
 * Chat attachment file size limit: 10 MB
 * Must match backend MAX_CHAT_ATTACHMENT_FILE_SIZE in service/chat/constants.py
 * Used in: useChatFileUpload.ts for client-side validation before upload
 */
export const CHAT_ATTACHMENT_MAX_SIZE_BYTES = 10 * 1024 * 1024;

/**
 * Human-readable max size for chat attachments (for error messages)
 * Derived from CHAT_ATTACHMENT_MAX_SIZE_BYTES to avoid drift.
 */
export const CHAT_ATTACHMENT_MAX_SIZE_MB = CHAT_ATTACHMENT_MAX_SIZE_BYTES / (1024 * 1024);

/**
 * Chat attachments accept attribute value
 * Supports images and documents that can be attached to chat messages
 * This is the single source of truth for chat attachment validation
 * Used in: ChatInput paste handling, file upload validation
 *
 * Matches backend ALLOWED_CHAT_ATTACHMENT_CONTENT_TYPES in service/chat/constants.py
 */
export const CHAT_ATTACHMENTS_ACCEPT_FORMATS =
  '.doc,.docx,.pdf,.txt,.png,.jpg,.jpeg,.gif,.webp,.xlsx,.csv,.html,.md,.rtf,.json,.odt,.epub,.pptx,.xml,.xsd';

/**
 * Set of allowed extensions for chat attachments (lowercase, without dots)
 * Derived from CHAT_ATTACHMENTS_ACCEPT_FORMATS for efficient validation
 */
export const CHAT_ATTACHMENTS_ALLOWED_EXTENSIONS = new Set(
  CHAT_ATTACHMENTS_ACCEPT_FORMATS.split(',').map((ext) =>
    ext.replace('.', '').toLowerCase()
  )
);

/**
 * MIME types accepted for chat attachments.
 * Must match backend ALLOWED_CHAT_ATTACHMENT_CONTENT_TYPES in
 * domains/chat/service/constants.py.
 *
 * Used as a fallback in client-side validation when clipboard-pasted files
 * arrive without a filename extension (common for screenshots).
 */
export const CHAT_ATTACHMENTS_ALLOWED_MIME_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/csv',
  'text/plain',
  'text/html',
  'text/markdown',
  'application/rtf',
  'application/json',
  'application/vnd.oasis.opendocument.text',
  'application/epub+zip',
  'application/xml',
  'text/xml',
]);

/**
 * Default filename commonly used by browsers when pasting images from clipboard.
 * Used in: useDirectUpload.ts, useHandleFileChangeOld.ts to auto-rename duplicate clipboard pastes
 */
export const CLIPBOARD_PASTE_FILENAME = 'image.png';
